import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  Target, 
  Plus, 
  Calendar, 
  Edit, 
  Trash2, 
  Filter, 
  Search, 
  CheckCircle, 
  Clock, 
  Flag,
  ChevronDown,
  ChevronUp,
  X
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useCurrency } from "../contexts/CurrencyContext";

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

// Task Form Modal Component
const TaskFormModal = ({ task, goalsMap, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    category: task?.category || 'financial',
    priority: task?.priority || 'medium',
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    linked_goal_id: task?.linked_goal_id || '',
    estimated_hours: task?.estimated_hours || '',
    tags: task?.tags?.join(', ') || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error(t('actionPlan.titleRequired') || 'Task title is required');
      return;
    }

    const submitData = {
      ...formData,
      due_date: formData.due_date ? new Date(formData.due_date + 'T00:00:00.000Z').toISOString() : null,
      estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
      tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
      linked_goal_id: formData.linked_goal_id || null
    };

    onSave(submitData);
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {task ? t('actionPlan.editTask') : t('actionPlan.addNewTask')}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">{t('actionPlan.taskTitle')} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={t('actionPlan.taskTitlePlaceholder')}
                required
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">{t('actionPlan.description')}</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('actionPlan.descriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">{t('actionPlan.category')}</Label>
              <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="the_one_thing">{t('actionPlan.categories.theOneThing')}</SelectItem>
                  <SelectItem value="financial">{t('actionPlan.categories.financial')}</SelectItem>
                  <SelectItem value="career">{t('actionPlan.categories.career')}</SelectItem>
                  <SelectItem value="personal">{t('actionPlan.categories.personal')}</SelectItem>
                  <SelectItem value="health">{t('actionPlan.categories.health')}</SelectItem>
                  <SelectItem value="learning">{t('actionPlan.categories.learning')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div>
              <Label htmlFor="priority">{t('actionPlan.priority')}</Label>
              <Select value={formData.priority} onValueChange={(value) => handleChange('priority', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-red-500" />
                      {t('actionPlan.priorities.high')}
                    </span>
                  </SelectItem>
                  <SelectItem value="medium">
                    <span className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-yellow-500" />
                      {t('actionPlan.priorities.medium')}
                    </span>
                  </SelectItem>
                  <SelectItem value="low">
                    <span className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-green-500" />
                      {t('actionPlan.priorities.low')}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Due Date */}
            <div>
              <Label htmlFor="due_date">{t('actionPlan.dueDate')}</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
              />
            </div>

            {/* Linked Goal */}
            {Object.keys(goalsMap).length > 0 && (
              <div>
                <Label htmlFor="linked_goal_id">{t('actionPlan.linkedGoal')}</Label>
                <Select value={formData.linked_goal_id || "none"} onValueChange={(value) => handleChange('linked_goal_id', value === "none" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('actionPlan.selectGoal')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('actionPlan.noLinkedGoal')}</SelectItem>
                    {Object.entries(goalsMap).map(([id, title]) => (
                      <SelectItem key={id} value={id}>{title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Estimated Hours */}
            <div>
              <Label htmlFor="estimated_hours">{t('actionPlan.estimatedHours')}</Label>
              <Input
                id="estimated_hours"
                type="number"
                step="0.5"
                min="0"
                value={formData.estimated_hours}
                onChange={(e) => handleChange('estimated_hours', e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t('actionPlan.cancel')}
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {task ? t('actionPlan.updateTask') : t('actionPlan.saveTask')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main Action Plan Module Component
const ActionPlanModule = () => {
  const { t } = useTranslation();
  const { formatAmount, activeCurrency } = useCurrency();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    priority: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState('due_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCompleted, setShowCompleted] = useState(false);
  const [goalsMap, setGoalsMap] = useState({});
  
  // Fetch goals to show linked goal names
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const authToken = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        if (!authToken) return;
        
        const response = await axios.get(`${API}/goals/objectives`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const goals = response.data.objectives || [];
        const map = {};
        goals.forEach(goal => {
          map[goal.objective_id] = goal.objective_title;
        });
        setGoalsMap(map);
      } catch (error) {
        console.error('Error fetching goals:', error);
      }
    };
    fetchGoals();
  }, []);
  
  // Load tasks from localStorage initially
  useEffect(() => {
    const savedTasks = localStorage.getItem('prosperMind_tasks');
    if (savedTasks) {
      try {
        const parsedTasks = JSON.parse(savedTasks);
        setTasks(parsedTasks);
      } catch (error) {
        console.error('Error parsing saved tasks:', error);
      }
    }
    setLoading(false);
  }, []);

  // Save tasks to localStorage whenever tasks change
  useEffect(() => {
    localStorage.setItem('prosperMind_tasks', JSON.stringify(tasks));
  }, [tasks]);

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  const addTask = (taskData) => {
    // Check for "The One Thing" constraint
    if (taskData.category === 'the_one_thing') {
      const existingOneThingTask = tasks.find(task => 
        task.category === 'the_one_thing' && task.status !== 'completed'
      );
      if (existingOneThingTask) {
        toast.error(t('actionPlan.oneThingExists') || 'You can only have one active "The One Thing" task. Complete or remove the existing one first.');
        return;
      }
    }
    
    const newTask = {
      id: generateId(),
      ...taskData,
      status: 'pending',
      created_at: new Date().toISOString(),
      completed_at: null
    };
    setTasks(prev => [...prev, newTask]);
    setShowAddForm(false);
    toast.success(t('actionPlan.taskAdded'));
  };

  const updateTask = (taskData) => {
    // Check for "The One Thing" constraint when updating
    if (taskData.category === 'the_one_thing') {
      const existingOneThingTask = tasks.find(task => 
        task.category === 'the_one_thing' && 
        task.status !== 'completed' &&
        task.id !== editingTask.id
      );
      if (existingOneThingTask) {
        toast.error(t('actionPlan.oneThingExists') || 'You can only have one active "The One Thing" task.');
        return;
      }
    }
    
    setTasks(prev => prev.map(task => 
      task.id === editingTask.id 
        ? { ...task, ...taskData, updated_at: new Date().toISOString() }
        : task
    ));
    setEditingTask(null);
    setShowAddForm(false);
    toast.success(t('actionPlan.taskUpdated'));
  };

  const deleteTask = (taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
    toast.success(t('actionPlan.taskDeleted'));
  };

  const toggleTaskStatus = (taskId) => {
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        const newStatus = task.status === 'completed' ? 'pending' : 'completed';
        return {
          ...task,
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null
        };
      }
      return task;
    }));
  };

  const handleFilterChange = (key, value) => {
    const filterValue = value === "all" ? "" : value;
    setFilters(prev => ({ ...prev, [key]: filterValue }));
  };

  // Filter and sort tasks
  const filteredTasks = tasks
    .filter(task => {
      if (!showCompleted && task.status === 'completed') return false;
      if (filters.category && task.category !== filters.category) return false;
      if (filters.priority && task.priority !== filters.priority) return false;
      if (filters.status && task.status !== filters.status) return false;
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        return task.title.toLowerCase().includes(searchTerm) ||
               (task.description || '').toLowerCase().includes(searchTerm);
      }
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'due_date') {
        const dateA = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
        const dateB = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
        comparison = dateA - dateB;
      } else if (sortBy === 'priority') {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
      } else if (sortBy === 'created_at') {
        comparison = new Date(a.created_at) - new Date(b.created_at);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Get "The One Thing" task
  const theOneThing = tasks.find(task => task.category === 'the_one_thing' && task.status !== 'completed');

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'the_one_thing': return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'financial': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'career': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'personal': return 'bg-pink-100 text-pink-700 border-pink-300';
      case 'health': return 'bg-red-100 text-red-700 border-red-300';
      case 'learning': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-black text-2xl font-bold">
              <Target className="w-5 h-5" />
              {t('actionPlan.title')}
            </CardTitle>
            <CardDescription>{t('actionPlan.description')}</CardDescription>
          </div>
          <Button
            onClick={() => { setEditingTask(null); setShowAddForm(true); }}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('actionPlan.addTask')}
          </Button>
        </div>

        {/* The One Thing Highlight */}
        {theOneThing && (
          <div className="mt-4 p-4 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl border-2 border-purple-300">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-purple-600 font-bold text-sm uppercase tracking-wide">
                    {t('actionPlan.theOneThing')}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-purple-900">{theOneThing.title}</h3>
                {theOneThing.description && (
                  <p className="text-sm text-purple-700 mt-1">{theOneThing.description}</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => toggleTaskStatus(theOneThing.id)}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {t('actionPlan.complete')}
              </Button>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Category Filter */}
              <Select value={filters.category || "all"} onValueChange={(value) => handleFilterChange('category', value)}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder={t('actionPlan.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('actionPlan.allCategories')}</SelectItem>
                  <SelectItem value="the_one_thing">{t('actionPlan.categories.theOneThing')}</SelectItem>
                  <SelectItem value="financial">{t('actionPlan.categories.financial')}</SelectItem>
                  <SelectItem value="career">{t('actionPlan.categories.career')}</SelectItem>
                  <SelectItem value="personal">{t('actionPlan.categories.personal')}</SelectItem>
                  <SelectItem value="health">{t('actionPlan.categories.health')}</SelectItem>
                  <SelectItem value="learning">{t('actionPlan.categories.learning')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={filters.priority || "all"} onValueChange={(value) => handleFilterChange('priority', value)}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder={t('actionPlan.allPriorities')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('actionPlan.allPriorities')}</SelectItem>
                  <SelectItem value="high">{t('actionPlan.priorities.high')}</SelectItem>
                  <SelectItem value="medium">{t('actionPlan.priorities.medium')}</SelectItem>
                  <SelectItem value="low">{t('actionPlan.priorities.low')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Show Completed Toggle */}
              <Button
                variant={showCompleted ? "default" : "outline"}
                size="sm"
                onClick={() => setShowCompleted(!showCompleted)}
                className={showCompleted ? "bg-purple-600 hover:bg-purple-700" : ""}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {t('actionPlan.showCompleted')}
              </Button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder={t('actionPlan.searchTasks')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-48"
              />
            </div>
          </div>

          {/* Task List */}
          <Card className="bg-white">
            {loading ? (
              <div className="p-8 text-center text-gray-500">{t('actionPlan.loading')}</div>
            ) : filteredTasks.length === 0 ? (
              <div className="p-8 text-center text-gray-500">{t('actionPlan.noTasks')}</div>
            ) : (
              <div className="divide-y">
                {filteredTasks.map((task) => (
                  <div 
                    key={task.id} 
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      task.status === 'completed' ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <button
                          onClick={() => toggleTaskStatus(task.id)}
                          className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            task.status === 'completed'
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'border-gray-300 hover:border-purple-500'
                          }`}
                        >
                          {task.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                              {task.title}
                            </span>
                            <Badge className={`text-xs ${getCategoryColor(task.category)}`}>
                              {t(`actionPlan.categories.${task.category}`)}
                            </Badge>
                            <Badge className={`text-xs ${getPriorityColor(task.priority)}`}>
                              <Flag className="w-3 h-3 mr-1" />
                              {t(`actionPlan.priorities.${task.priority}`)}
                            </Badge>
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            )}
                            {task.linked_goal_id && goalsMap[task.linked_goal_id] && (
                              <span className="flex items-center gap-1">
                                <Target className="w-3 h-3" />
                                {goalsMap[task.linked_goal_id]}
                              </span>
                            )}
                            {task.estimated_hours && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {task.estimated_hours}h
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingTask(task); setShowAddForm(true); }}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteTask(task.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Add/Edit Task Modal */}
        {showAddForm && (
          <TaskFormModal
            task={editingTask}
            goalsMap={goalsMap}
            onSave={editingTask ? updateTask : addTask}
            onCancel={() => { setShowAddForm(false); setEditingTask(null); }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ActionPlanModule;
